/**
 * @component widget-flag-picker-modal
 * @css profile-widget-flag-bar.css
 * @primitives .btn.btn-primary, .btn.btn-secondary, .form-group, .form-control
 */
import { escapeHtml } from '../utils/html-utils.js';

const FLAG_REASONS = ['inappropriate', 'copyright', 'spam', 'broken', 'privacy'];

const FLAG_REASON_LABELS = {
  inappropriate: 'Inappropriate content',
  copyright: 'Copyright concern',
  spam: 'Spam',
  broken: 'Broken or misleading',
  privacy: 'Privacy concern'
};

class WidgetFlagPickerModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this._step = 'widgets';
    this._widgets = [];
    this._selectedWidget = null;
    this._onSelect = null;
    this._keydownHandler = this._keydownHandler.bind(this);
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._keydownHandler);
    if (this.isOpen) {
      document.body.style.overflow = '';
    }
  }

  open(options = {}) {
    this._widgets = Array.isArray(options.widgets) ? options.widgets : [];
    this._onSelect = typeof options.onSelect === 'function' ? options.onSelect : null;
    this._step = 'widgets';
    this._selectedWidget = null;
    this.isOpen = true;
    document.body.style.overflow = 'hidden';
    document.removeEventListener('keydown', this._keydownHandler);
    this.render();
    this.attachEventListeners();
    document.addEventListener('keydown', this._keydownHandler);

    requestAnimationFrame(() => {
      this._focusInitialControl();
    });
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this._step = 'widgets';
    this._widgets = [];
    this._selectedWidget = null;
    this._onSelect = null;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._keydownHandler);
    this.render();
    this.attachEventListeners();
    this.dispatchEvent(new CustomEvent('widget-flag-picker-close', { bubbles: true }));
  }

  _focusInitialControl() {
    if (this._step === 'reason') {
      this.querySelector('#widgetFlagReason')?.focus();
      return;
    }
    this.querySelector('.widget-flag-picker-item')?.focus();
  }

  _goToReasonStep(widget) {
    this._selectedWidget = widget;
    this._step = 'reason';
    this.render();
    this.attachEventListeners();
    requestAnimationFrame(() => this._focusInitialControl());
  }

  _goToWidgetStep() {
    this._selectedWidget = null;
    this._step = 'widgets';
    this.render();
    this.attachEventListeners();
    requestAnimationFrame(() => this._focusInitialControl());
  }

  _keydownHandler(event) {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this._step === 'reason') {
        this._goToWidgetStep();
        return;
      }
      this.close();
    }
  }

  _getTitle() {
    if (this._step === 'reason' && this._selectedWidget) {
      return `Flag “${this._selectedWidget.name}”`;
    }
    return 'Which widget do you want to flag?';
  }

  _renderWidgetStep() {
    if (!this._widgets.length) {
      return `
        <div class="widget-flag-picker-empty">
          <p class="form-text">No widgets are available to flag.</p>
        </div>
      `;
    }

    return `
      <p class="modal-description">Pick the widget you want to report.</p>
      <div class="widget-flag-picker-list" role="listbox" aria-label="Widgets on this profile">
        ${this._widgets.map((widget) => `
          <button
            type="button"
            class="widget-flag-picker-item card"
            data-section-id="${escapeHtml(widget.sectionId)}"
            role="option"
          >
            <span class="widget-flag-picker-item-icon" aria-hidden="true">${escapeHtml(widget.icon || '🧩')}</span>
            <span class="widget-flag-picker-item-name">${escapeHtml(widget.name)}</span>
          </button>
        `).join('')}
      </div>
    `;
  }

  _renderReasonStep() {
    const options = FLAG_REASONS.map((reason) => `
      <option value="${escapeHtml(reason)}">${escapeHtml(FLAG_REASON_LABELS[reason])}</option>
    `).join('');

    return `
      <p class="modal-description">Tell us why you are flagging this widget.</p>
      <form class="widget-flag-reason-form" id="widgetFlagReasonForm">
        <div class="form-group">
          <label class="form-label" for="widgetFlagReason">Reason</label>
          <select id="widgetFlagReason" class="form-control" required>
            <option value="" disabled selected>Select a reason…</option>
            ${options}
          </select>
        </div>
        <div class="widget-flag-picker-actions">
          <button type="button" class="btn btn-secondary" data-action="reason-back">Back</button>
          <button type="submit" class="btn btn-primary">Submit flag</button>
        </div>
      </form>
    `;
  }

  _renderBody() {
    if (this._step === 'reason') {
      return this._renderReasonStep();
    }
    return this._renderWidgetStep();
  }

  render() {
    this.innerHTML = `
      <div class="widget-flag-picker-modal ${this.isOpen ? 'open' : ''}" id="widgetFlagPickerModal">
        <div class="modal-backdrop" id="widgetFlagPickerBackdrop"></div>
        <div class="modal-container" role="dialog" aria-modal="true" aria-labelledby="widgetFlagPickerTitle">
          <div class="modal-header">
            <h3 id="widgetFlagPickerTitle">${escapeHtml(this._getTitle())}</h3>
            <button type="button" class="close-btn" id="widgetFlagPickerCloseBtn" aria-label="Close">×</button>
          </div>
          <div class="modal-body">
            ${this.isOpen ? this._renderBody() : ''}
          </div>
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    this.querySelector('#widgetFlagPickerBackdrop')?.addEventListener('click', () => this.close());
    this.querySelector('#widgetFlagPickerCloseBtn')?.addEventListener('click', () => this.close());

    this.querySelectorAll('.widget-flag-picker-item').forEach((btn) => {
      btn.addEventListener('click', () => {
        const sectionId = btn.dataset.sectionId;
        const widget = this._widgets.find((w) => w.sectionId === sectionId);
        if (!widget) return;
        this._goToReasonStep(widget);
      });
    });

    this.querySelector('[data-action="reason-back"]')?.addEventListener('click', () => {
      this._goToWidgetStep();
    });

    this.querySelector('#widgetFlagReasonForm')?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const select = this.querySelector('#widgetFlagReason');
      const reason = select?.value;
      if (!reason || !FLAG_REASONS.includes(reason) || !this._selectedWidget || !this._onSelect) {
        return;
      }

      try {
        await this._onSelect(this._selectedWidget, reason);
        this.close();
      } catch (err) {
        alert(err.message || 'Could not submit flag');
      }
    });
  }
}

customElements.define('widget-flag-picker-modal', WidgetFlagPickerModal);

export { WidgetFlagPickerModal };
